/**
 * Content 도메인 Query Hook Wrapper
 *
 * Factory를 사용하여 데이터를 조회합니다.
 */

import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useCallback} from 'react';
import {CONTENT_QUERY_KEY_MAP} from '../api/content/constant';
import {contentFactory} from '../factory/contentFactory';

// ============================================
// useContentHomeAboutQuery
// ============================================
export function useContentHomeAboutQuery(
  ...args: Parameters<typeof contentFactory.contentHomeAbout>
) {
  return useQuery(contentFactory.contentHomeAbout(...args));
}

useContentHomeAboutQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeAbout>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeAbout(...args)];

// ============================================
// useContentHomeInfoQuery (refetch 패턴)
// ============================================
export function useContentHomeInfoQuery(
  ...args: Parameters<typeof contentFactory.contentHomeInfo>
) {
  const [variables] = args;
  const queryClient = useQueryClient();
  const result = useQuery(contentFactory.contentHomeInfo(...args));

  const refetch = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeInfo(variables),
    });
  }, [queryClient, variables]);

  return {...result, refetch};
}

useContentHomeInfoQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeInfo>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeInfo(...args)];

// ============================================
// useContentHomeNoticeQuery
// ============================================
export function useContentHomeNoticeQuery(
  ...args: Parameters<typeof contentFactory.contentHomeNotice>
) {
  return useQuery(contentFactory.contentHomeNotice(...args));
}

useContentHomeNoticeQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeNotice>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeNotice(...args)];

// ============================================
// useContentHomeOverviewQuery (refetch 패턴)
// ============================================
export function useContentHomeOverviewQuery(
  ...args: Parameters<typeof contentFactory.contentHomeOverview>
) {
  const [variables] = args;
  const queryClient = useQueryClient();
  const result = useQuery(contentFactory.contentHomeOverview(...args));

  const refetch = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeOverview(variables),
    });
  }, [queryClient, variables]);

  return {...result, refetch};
}

useContentHomeOverviewQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeOverview>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeOverview(...args)];

// ============================================
// useContentHomeProductListQuery
// ============================================
export function useContentHomeProductListQuery(
  ...args: Parameters<typeof contentFactory.contentHomeProductList>
) {
  return useQuery(contentFactory.contentHomeProductList(...args));
}

useContentHomeProductListQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeProductList>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeProductList(...args)];

// ============================================
// useContentHomeRecommendQuery
// ============================================
export function useContentHomeRecommendQuery(
  ...args: Parameters<typeof contentFactory.contentHomeRecommend>
) {
  return useQuery(contentFactory.contentHomeRecommend(...args));
}

useContentHomeRecommendQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentHomeRecommend>
) => [...CONTENT_QUERY_KEY_MAP.contentHomeRecommend(...args)];

// ============================================
// useContentProductCursorIndexQuery
// ============================================
export function useContentProductCursorIndexQuery(
  ...args: Parameters<typeof contentFactory.contentProductCursorIndex>
) {
  return useQuery(contentFactory.contentProductCursorIndex(...args));
}

useContentProductCursorIndexQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentProductCursorIndex>
) => [...CONTENT_QUERY_KEY_MAP.contentProductCursorIndex(...args)];

// ============================================
// useContentSetListQuery
// ============================================
export function useContentSetListQuery(
  ...args: Parameters<typeof contentFactory.contentSetList>
) {
  return useQuery(contentFactory.contentSetList(...args));
}

useContentSetListQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentSetList>
) => [...CONTENT_QUERY_KEY_MAP.contentSetList(...args)];

// ============================================
// useContentSetOverviewQuery
// ============================================
export function useContentSetOverviewQuery(
  ...args: Parameters<typeof contentFactory.contentSetOverview>
) {
  return useQuery(contentFactory.contentSetOverview(...args));
}

useContentSetOverviewQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.contentSetOverview>
) => [...CONTENT_QUERY_KEY_MAP.contentSetOverview(...args)];

// ============================================
// useSimpleSeriesSingleInfoQuery
// ============================================
export function useSimpleSeriesSingleInfoQuery(
  ...args: Parameters<typeof contentFactory.simpleSeriesSingleInfo>
) {
  return useQuery(contentFactory.simpleSeriesSingleInfo(...args));
}

useSimpleSeriesSingleInfoQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.simpleSeriesSingleInfo>
) => [...CONTENT_QUERY_KEY_MAP.simpleSeriesSingleInfo(...args)];

// ============================================
// useInfiniteContentHomeNoticeQuery
// ============================================
export function useInfiniteContentHomeNoticeQuery(
  ...args: Parameters<typeof contentFactory.infiniteContentHomeNotice>
) {
  return useInfiniteQuery(contentFactory.infiniteContentHomeNotice(...args));
}

useInfiniteContentHomeNoticeQuery.getKey = (
  ...args: Parameters<typeof CONTENT_QUERY_KEY_MAP.infiniteContentHomeNotice>
) => [...CONTENT_QUERY_KEY_MAP.infiniteContentHomeNotice(...args)];

// ============================================
// useInfiniteContentHomeProductListQuery
// ============================================
export function useInfiniteContentHomeProductListQuery(
  ...args: Parameters<typeof contentFactory.infiniteContentHomeProductList>
) {
  return useInfiniteQuery(
    contentFactory.infiniteContentHomeProductList(...args)
  );
}

useInfiniteContentHomeProductListQuery.getKey = (
  ...args: Parameters<
    typeof CONTENT_QUERY_KEY_MAP.infiniteContentHomeProductList
  >
) => [...CONTENT_QUERY_KEY_MAP.infiniteContentHomeProductList(...args)];
